import React, { useEffect, useState } from "react";
import { Alert, Card, CardBody, Col, Container, Row, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import AdatisLogo from "../../Components/Common/AdatisLogo";
import { consumeGoogleOAuthIntent } from "../../Components/Common/GoogleAuthButton";
import withRouter from "../../Components/Common/withRouter";
import {
  getPreferredTenantId,
  isMembershipEnterable,
  StudioMembership,
} from "../../helpers/identity_storage";
import { resolveAuthClientError } from "../../helpers/authClientError";
import { resolvePostAuthRedirect } from "../../helpers/postAuthRedirect";
import { loginSuccess } from "../../slices/auth/login/reducer";
import {
  exchangeGoogleCode,
  getAuthSession,
  linkGoogleAccount,
  selectTenant,
} from "../../services/authService";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";

const GOOGLE_SIGNUP_KEY = "adatis.googleSignup";

export type GoogleSignupDraft = {
  identity_token: string;
  email: string;
  name: string;
};

export function storeGoogleSignupDraft(draft: GoogleSignupDraft): void {
  try {
    sessionStorage.setItem(GOOGLE_SIGNUP_KEY, JSON.stringify(draft));
  } catch {
    // ignore
  }
}

export function consumeGoogleSignupDraft(): GoogleSignupDraft | null {
  try {
    const raw = sessionStorage.getItem(GOOGLE_SIGNUP_KEY);
    sessionStorage.removeItem(GOOGLE_SIGNUP_KEY);

    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as GoogleSignupDraft;
  } catch {
    return null;
  }
}

const finishAuthenticatedSession = (
  dispatch: ReturnType<typeof useDispatch>,
  navigate: ReturnType<typeof useNavigate>,
  returnQuery?: string | null
) => {
  const session = getAuthSession();

  if (session) {
    dispatch(loginSuccess(session));
  }

  navigate(resolvePostAuthRedirect(null, returnQuery));
};

const GoogleCallback = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const code = searchParams.get("code");
      const oauthError = searchParams.get("error");

      if (oauthError) {
        setErrorMessage(t("auth.google.toast.cancelled"));
        return;
      }

      if (!code) {
        setErrorMessage(t("auth.google.toast.missingCode"));
        return;
      }

      const intent = consumeGoogleOAuthIntent();

      try {
        if (intent === "link") {
          await linkGoogleAccount(code);

          if (cancelled) {
            return;
          }

          toast.success(t("settings.account.google.toast.linked"));
          navigate("/settings/account");
          return;
        }

        const response = await exchangeGoogleCode(code, intent);

        if (cancelled) {
          return;
        }

        if (!response.success || !response.data) {
          setErrorMessage(t("auth.google.toast.exchangeFailed"));
          return;
        }

        const data = response.data;

        if (
          data.type === "google_signup_required" ||
          data.type === "studio_creation_required"
        ) {
          storeGoogleSignupDraft({
            identity_token: data.identity_token,
            email: data.email,
            name: data.name || "",
          });
          const continuePath =
            data.type === "studio_creation_required"
              ? "/register?continue=1"
              : "/register?google=1";
          toast.success(
            response.message ||
              (data.type === "studio_creation_required"
                ? t("auth.google.toast.studioCreationContinue")
                : t("auth.google.toast.signupContinue"))
          );
          navigate(continuePath);
          return;
        }

        if (data.type === "membership_selection_required") {
          const preferredTenantId = getPreferredTenantId();
          const memberships = (data.memberships || []) as StudioMembership[];
          const preferred = preferredTenantId
            ? memberships.find(
                (item) =>
                  item.tenant_id === preferredTenantId &&
                  isMembershipEnterable(item)
              )
            : null;

          if (preferred) {
            try {
              const selected = await selectTenant(preferred.tenant_id);
              if (selected.success && selected.data?.access_token) {
                finishAuthenticatedSession(
                  dispatch,
                  navigate,
                  searchParams.get("return")
                );
                return;
              }
            } catch {
              // fall through to select-studio
            }
          }

          const returnQuery = searchParams.get("return");
          navigate(
            returnQuery
              ? `/select-studio?return=${encodeURIComponent(returnQuery)}`
              : "/select-studio"
          );
          return;
        }

        if ("access_token" in data && data.access_token) {
          finishAuthenticatedSession(
            dispatch,
            navigate,
            searchParams.get("return")
          );
          return;
        }

        setErrorMessage(t("auth.google.toast.exchangeFailed"));
      } catch (error) {
        if (cancelled) {
          return;
        }

        const resolved = resolveAuthClientError(
          error,
          t("auth.google.toast.exchangeFailed")
        );

        setErrorMessage(resolved.message);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, navigate, searchParams, t]);

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4">
                  <CardBody className="p-4 text-center">
                    <AdatisLogo />
                    <h5 className="mt-3">{t("auth.google.callback.title")}</h5>
                    {errorMessage ? (
                      <>
                        <Alert color="danger" className="mt-3 text-start">
                          {errorMessage}
                        </Alert>
                        <p className="mb-0">
                          <Link to="/login" className="fw-semibold">
                            {t("auth.google.callback.backToLogin")}
                          </Link>
                        </p>
                      </>
                    ) : (
                      <div className="mt-4">
                        <Spinner color="success" />
                        <p className="text-muted mt-3 mb-0">
                          {t("auth.google.callback.loading")}
                        </p>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default withRouter(GoogleCallback);
